import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Zap, MessageSquare, UserPlus, AtSign, TrendingUp, TrendingDown } from "lucide-react";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { EnablePushBanner } from "@/components/WebPushBridge";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d`;
  return d.toLocaleDateString();
};

const KIND_META = {
  tip: {
    Icon: Zap,
    color: "text-[#FF007F]",
    fill: "#FF007F",
    label: "boosted your video",
  },
  follow: {
    Icon: UserPlus,
    color: "text-[#39FF14]",
    fill: "#39FF14",
    label: "started following you",
  },
  comment: {
    Icon: MessageSquare,
    color: "text-[#00FFFF]",
    fill: "#00FFFF",
    label: "commented on your video",
  },
  mention: {
    Icon: AtSign,
    color: "text-[#FFB800]",
    fill: "#FFB800",
    label: "mentioned you in a comment",
  },
  rank_up: {
    Icon: TrendingUp,
    color: "text-[#39FF14]",
    fill: "#39FF14",
    label: "leaderboard update",
  },
  rank_down: {
    Icon: TrendingDown,
    color: "text-[#FF007F]",
    fill: "#FF007F",
    label: "leaderboard update",
  },
};

export default function Notifications() {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const load = () => {
    api
      .get("/notifications?limit=50")
      .then((r) => setItems(r.data.notifications || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) return undefined;
    load();
    // Mark all as read on entering the page
    api.post("/notifications/mark-read", { all: true }).catch(() => {});
    // Live-append when a new notification arrives
    return subscribe((evt) => {
      if (evt.type === "notification_new" && evt.notification) {
        setItems((prev) => [evt.notification, ...prev]);
      }
    });
  }, [user, subscribe]);

  const handleMarkAll = async () => {
    if (marking) return;
    setMarking(true);
    try {
      await api.post("/notifications/mark-read", { all: true });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } finally {
      setMarking(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50 font-mono text-xs uppercase tracking-widest">
        sign in to see notifications
      </div>
    );
  }

  return (
    <div className="min-h-screen max-w-2xl mx-auto px-6 py-10">
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Bell className="h-5 w-5 text-[#39FF14]" strokeWidth={1.8} />
          <h1
            className="font-display font-black text-3xl tracking-tight glitch"
            data-text="NOTIFICATIONS"
          >
            NOTIFICATIONS
          </h1>
        </div>
        {items.some((n) => !n.read) && (
          <button
            data-testid="mark-all-read-btn"
            onClick={handleMarkAll}
            className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-[#39FF14]"
          >
            mark all read
          </button>
        )}
      </header>

      <EnablePushBanner />

      {loading ? (
        <div className="py-20 flex items-center justify-center">
          <div className="h-6 w-6 border-2 border-[#39FF14] border-t-transparent animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="notifications-empty"
          className="py-20 text-center font-mono text-xs uppercase tracking-widest text-white/30"
        >
          // no notifications yet
        </div>
      ) : (
        <ul data-testid="notifications-list" className="space-y-2">
          {items.map((n) => {
            const meta = KIND_META[n.kind] || KIND_META.follow;
            const { Icon, color, fill, label } = meta;
            const inner = (
              <div
                data-testid={`notification-item-${n.id}`}
                className={`flex items-start gap-3 p-4 border transition-colors cursor-pointer ${
                  n.read
                    ? "border-white/5 bg-[#050505] hover:border-white/15"
                    : "border-[#39FF14]/30 bg-[#0A0A0C] hover:border-[#39FF14]/60"
                }`}
              >
                {n.actor_picture ? (
                  <img
                    src={picUrl(n.actor_picture)}
                    alt=""
                    className="h-10 w-10 object-cover flex-shrink-0 border border-white/20"
                  />
                ) : (
                  <div className={`h-10 w-10 flex-shrink-0 flex items-center justify-center bg-black border border-white/20 ${color}`}>
                    <Icon className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  {n.kind === "rank_up" || n.kind === "rank_down" ? (
                    <p className="text-sm text-white leading-tight font-semibold">
                      {n.message}
                    </p>
                  ) : (
                    <p className="text-sm text-white leading-tight">
                      <span className="font-semibold">
                        {n.actor_name || "Someone"}
                      </span>{" "}
                      <span className="text-white/70">{label}</span>
                      {n.kind === "tip" && n.amount_cents != null && (
                        <span
                          className="ml-1 font-display font-black text-[#FF007F]"
                        >
                          +${(n.amount_cents / 100).toFixed(2)}
                        </span>
                      )}
                    </p>
                  )}
                  {n.message && n.kind !== "follow" && n.kind !== "rank_up" && n.kind !== "rank_down" && (
                    <p className="text-white/50 text-xs mt-1 italic truncate">
                      “{n.message}”
                    </p>
                  )}
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mt-1.5 flex items-center gap-2">
                    <Icon className={`h-3 w-3 ${color}`} fill={fill} strokeWidth={0} />
                    {timeAgo(n.created_at)} ago
                  </p>
                </div>

                {!n.read && (
                  <span
                    className="h-2 w-2 mt-2 bg-[#39FF14] shadow-[0_0_8px_rgba(57,255,20,0.8)] flex-shrink-0"
                    aria-label="unread"
                  />
                )}
              </div>
            );
            return (
              <li key={n.id}>
                {n.video_id ? (
                  <Link to={`/v/${n.video_id}`}>{inner}</Link>
                ) : n.kind === "rank_up" || n.kind === "rank_down" ? (
                  <Link to="/leaderboard">{inner}</Link>
                ) : n.actor_user_id ? (
                  // Route by actor_user_id — the notification payload does
                  // not carry the actor's username slug, and `actor_name`
                  // is a display name (spaces, caps) that would 404 the
                  // `/u/:slug` route. The `/api/users/{slug}` endpoint
                  // accepts either a username or a raw user_id.
                  <Link to={`/u/${n.actor_user_id}`}>{inner}</Link>
                ) : (
                  inner
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
